import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Uploads a file directly to Azure Blob Storage via a pre-signed SAS URL.
 *
 * Uses an HttpClient built from HttpBackend so that application-level auth and
 * tenant interceptors do not mutate the outbound request (they would corrupt the
 * SAS signature).
 */
@Injectable({ providedIn: 'root' })
export class BlobSasUploadService {
  private readonly http: HttpClient;

  constructor() {
    const handler = inject(HttpBackend);
    this.http = new HttpClient(handler);
  }

  public uploadToSasUrl(sasUrl: string, file: File): Observable<void> {
    const headers = new HttpHeaders({
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/octet-stream'
    });
    return this.http.put(sasUrl, file, { headers, observe: 'response' }).pipe(map(() => undefined));
  }

  /**
   * Deletes a blob directly via a pre-signed delete SAS URL. Uses the bypass
   * HttpClient so auth/tenant interceptors do not mutate the signed request.
   */
  public deleteViaSasUrl(sasUrl: string): Observable<void> {
    return this.http.delete(sasUrl, { observe: 'response' }).pipe(map(() => undefined));
  }

  /**
   * Downloads a file via a pre-signed read SAS URL by fetching the blob and
   * triggering a browser download with the given filename.
   */
  public downloadViaSasUrl(sasUrl: string, filename: string): Observable<void> {
    return this.http.get(sasUrl, { responseType: 'blob' }).pipe(
      map(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
    );
  }
}
